import { z } from "zod";

export const MAX_SPICES = 8;
export const MAX_QUANTITY_GRAM = 200;

const createTextureConfig = (res: number, channels: number) =>
  ({
    res,
    width: res,
    height: res,
    size: res * res,
    depth: MAX_SPICES,
    channels: 4, // RGBA
  } as const);

export const TEX_CONFIG = {
  LOW: createTextureConfig(256, 3), // only provide 3 channels for less sampling
  HIGH: createTextureConfig(512, 4),  // AO & Roughness baked into alpha channel
} as const;

export enum TexType {
  NORMAL = "normal",
  TEXTURE = "texture",
  FALLBACK = "fallback",
}

export const getTexConfig = (type: TexType) =>
  type === TexType.FALLBACK ? TEX_CONFIG.LOW : TEX_CONFIG.HIGH;

/*
  Daily value based on FDA nutrition facts
  https://www.fda.gov/food/nutrition-facts-label/daily-value-nutrition-and-supplement-facts-labels?utm_source=chatgpt.com
*/

// 1. Nutrition -> Source of truth
export const NUTRIENTS = [
  { id: 1008, name: "calories", dailyValue: { value: 2000, unit: "kcal" } },
  { id: 1004, name: "total_fat", dailyValue: { value: 78, unit: "g" } },
  { id: 1258, name: "saturated_fat", dailyValue: { value: 20, unit: "g" } },
  { id: 1257, name: "trans_fat", dailyValue: { value: 2, unit: "g" } },
  { id: 1253, name: "cholesterol", dailyValue: { value: 300, unit: "mg" } },
  { id: 1093, name: "sodium", dailyValue: { value: 2300, unit: "mg" } },
  { id: 1005, name: "total_carbohydrate", dailyValue: { value: 275, unit: "g" } }, //prettier-ignore
  { id: 1079, name: "dietary_fiber", dailyValue: { value: 28, unit: "g" } },
  { id: 2000, name: "total_sugars", dailyValue: { value: 50, unit: "g" } },
  { id: 1003, name: "protein", dailyValue: { value: 50, unit: "g" } },
  { id: 1114, name: "vitamin_d", dailyValue: { value: 20, unit: "µg" } },
  { id: 1087, name: "calcium", dailyValue: { value: 1300, unit: "mg" } },
  { id: 1089, name: "iron", dailyValue: { value: 18, unit: "mg" } },
  { id: 1092, name: "potassium", dailyValue: { value: 4700, unit: "mg" } },
] as const;

/* !!!! API !!!! */
export const VALID_NUTRIENT_IDS = NUTRIENTS.map((n) => n.id);

export const NutritionSchema = z
  .array(
    z.object({
      id: z.number(), // keeps as number
      name: z.string(),
      amount: z.number(),
      unit: z.string(),
    })
  )
  .length(NUTRIENTS.length)
  .superRefine((arr, ctx) => {
    const presentIds = arr.map((n) => n.id);
    for (const id of VALID_NUTRIENT_IDS) {
      if (!presentIds.includes(id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Missing nutrient with ID ${id}`,
        });
      }
    }
  });

export enum Units {
  TSP = "tsp",
  TBSP = "tbsp",
  GRAM = "gram",
}

export const PortionsSchema = z.object({
  [Units.TSP]: z.number(),
  [Units.TBSP]: z.number(),
  [Units.GRAM]: z.number().default(1), // fallback to 1 if missing -> ( it always does since its never defined )
});

export const APISchema = z.object({
  name: z.string(),
  id: z.number(),
  thumbnail: z.string(),
  textures: z.object({
    normal: z.string(),
    texture: z.string(),
    fallback: z.string(),
  }),
  shaderprops: z.object({
    colorIntensity: z.number(),
    scale: z.number(),
    norIntensity: z.number(),
    roughnessIntensity: z.number(),
    aoIntensity: z.number(),
  }),
  portions: PortionsSchema,
  nutrition: NutritionSchema,
});

export const CacheSchema = z.record(z.number(), APISchema);

/* !!!! FILTER !!!! */
export const BlendInfoSchema = z.object({
  author: z
    .string()
    .min(1, { message: "Name is required." })
    .max(20, { message: "Name cannot exceed 20 characters." }),
  title: z
    .string()
    .min(1, { message: "Author is required." })
    .max(20, { message: "Author cannot exceed 10 characters." }),
});

export const SpiceSchema = z.object({
  name: z.string(),
  quantity: z.number().min(0).max(MAX_QUANTITY_GRAM), // if grams default to 0
});

export const SpicesMapSchema = z
  .map(z.number({ error: () => "Id/Spice is missing" }), SpiceSchema) // id as number
  .refine((map) => map.size <= MAX_SPICES, {
    error: () => "Total number of spices cannot exceed " + MAX_SPICES + ".",
  })
  .refine(
    (map) => { const totalQuantity = Array.from(map.values()).reduce((sum, spice) => sum + (spice?.quantity ?? 0), 0); return totalQuantity <= MAX_QUANTITY_GRAM;}, //prettier-ignore
    {
      error: () =>
        "Jar is full and cannot exceed: " +
        MAX_QUANTITY_GRAM +
        "gram. Try shaking instead! ",
    }
  );

// codec for schema
export const codec = z.codec(
  z.record(z.string(), SpiceSchema), // validate the JSON object keys as strings
  SpicesMapSchema, // Map<number, SpicesObject>
  {
    decode: (obj) =>
      new Map(Object.entries(obj).map(([key, value]) => [Number(key), value])),
    encode: (s) =>
      Object.fromEntries(
        Array.from(s.entries()).map(([key, value]) => [Number(key), value])
      ),
  }
);

/* !!!! URL STATE VALIDATION !!!! */
export const UrlStateSchema = z
  .object({
    isFromSharedLink: z.boolean(),
    isShaken: z.boolean(),
    spices: SpicesMapSchema,
    blendInfo: z
      .object({
        title: z.string().nullable(),
        author: z.string().nullable(),
      })
      .nullable(),
    unit: z.nativeEnum(Units),
    quantity: z.number(),
  })
  .superRefine((data, ctx) => {
    // Rule 1: If from shared link, must be shaken
    if (data.isFromSharedLink && !data.isShaken) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["isShaken"],
        message: "Shareable links must have shaken state enabled",
      });
    }

    // Rule 2: If from shared link, must have spices
    if (data.isFromSharedLink && data.spices.size === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["spices"],
        message: "Shareable links must contain at least one spice",
      });
    }

    // Rule 3: If from shared link, must have blend info
    if (data.isFromSharedLink && !data.blendInfo?.title) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["blendInfo", "title"],
        message: "Shareable links must include a blend title",
      });
    }

    if (data.isFromSharedLink && !data.blendInfo?.author) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["blendInfo", "author"],
        message: "Shareable links must include an author name",
      });
    }

    // Rule 4: If not from shared link, state should be fresh (optional validation)
    // This ensures non-shareable URLs don't have inconsistent state
  });

/* !!!! LABEL !!!! */
export const LabelSchema = z.object({
  blendInfo: BlendInfoSchema,
  nutrition: NutritionSchema,
});

/* !!!! TYPES !!!! */
export type Nutrient = (typeof NUTRIENTS)[number];
export type NutrientId = Nutrient["id"];
export type DailyValue = Nutrient["dailyValue"];
export type NutritionEntry = z.infer<typeof NutritionSchema>;
export type APISpice = z.infer<typeof APISchema>;

export type BlendInfo = z.infer<typeof BlendInfoSchema>;
export type Spice = z.infer<typeof SpiceSchema>;
export type SpicesMap = z.infer<typeof SpicesMapSchema>;
export type UrlState = z.infer<typeof UrlStateSchema>;
export type LocalSpice = Omit<
  z.infer<typeof APISchema>,
  "nutrition" | "portions"
> & {
  // Nutrition is missing in local files (fetched from API)
  // Portions in local files only have tsp/tbsp (gram is defaulted later)
  portions: {
    tsp: number;
    tbsp: number;
    gram: number;
  };
};
export type Cache = z.infer<typeof CacheSchema> | Record<number, LocalSpice>;
